import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ColumnVisibility {
  product: boolean;
  qty: boolean;
  price: boolean;
  tax: boolean;
  discount: boolean;
  total: boolean;
}

interface LineItem {
  id: string;
  product_uuid?: string; // UUID of the original product (for tracking product instances)
  name: string;
  quantity: number;
  unit_cost: number;
  unit_price: number;
  markup: number;
  tax: number;
  discount: number;
  description: string;
  total: number;
  item_type?: number; // 1 = Product, 0 = Service
  supplier_id?: string | null;
  isCustom?: boolean;
}

interface Section {
  id: string;
  title: string;
  description: string;
  lineItems: LineItem[];
  column_visibility: ColumnVisibility;
  sections: Section[];
}

interface QuoteBuilderState {
  sections: Section[];
  lastAddedLineItemId: string | null;
}

const DEFAULT_COLUMN_VISIBILITY: ColumnVisibility = {
  product: true,
  qty: true,
  price: true,
  tax: false,
  discount: false,
  total: true,
};

const initialState: QuoteBuilderState = {
  sections: [
    {
      id: `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: '',
      description: '',
      lineItems: [],
      sections: [],
      column_visibility: { ...DEFAULT_COLUMN_VISIBILITY },
    },
  ],
  lastAddedLineItemId: null,
};

// Helper function to find a section by ID in nested structure
const findSectionById = (
  sections: Section[],
  sectionId: string,
): Section | null => {
  for (const section of sections) {
    if (section.id === sectionId) {
      return section;
    }
    // Recursively search in nested sections
    const found = findSectionById(section.sections, sectionId);
    if (found) {
      return found;
    }
  }
  return null;
};

// Helper function to remove a section by ID from nested structure
const removeSectionById = (
  sections: Section[],
  sectionId: string,
): Section[] => {
  return sections
    .filter((section) => section.id !== sectionId)
    .map((section) => ({
      ...section,
      sections: removeSectionById(section.sections, sectionId),
    }));
};

// Helper function to sanitize field values
export const sanitizeFieldValue = (field: keyof LineItem, value: any): any => {
  const numericFields = [
    'unit_cost',
    'unit_price',
    'quantity',
    'markup',
    'discount',
    'tax',
    'total',
    'item_type',
  ];

  if (!numericFields.includes(field)) {
    return value;
  }

  // Handle string inputs
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  // Handle number inputs
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? 0 : value;
  }

  // Default fallback
  return 0;
};

// Helper function to get sanitized calculation values
const getCalculationValues = (
  currentItem: LineItem,
  field: keyof LineItem,
  safeValue: any,
) => {
  const getSafeNumber = (itemValue: any) => {
    if (typeof itemValue === 'string') return parseFloat(itemValue) || 0;
    if (typeof itemValue === 'number') return itemValue || 0;
    return 0;
  };

  return {
    quantity:
      field === 'quantity' ? safeValue : getSafeNumber(currentItem.quantity),
    unitPrice:
      field === 'unit_price'
        ? safeValue
        : getSafeNumber(currentItem.unit_price),
    unitCost:
      field === 'unit_cost' ? safeValue : getSafeNumber(currentItem.unit_cost),
    markup: field === 'markup' ? safeValue : getSafeNumber(currentItem.markup),
    discount:
      field === 'discount' ? safeValue : getSafeNumber(currentItem.discount),
    tax: field === 'tax' ? safeValue : getSafeNumber(currentItem.tax),
  };
};

// Helper function to calculate total
const calculateTotal = (
  values: ReturnType<typeof getCalculationValues>,
): number => {
  const { quantity, unitPrice, markup, discount, tax } = values;

  const total = unitPrice * quantity;

  // Ensure result is valid
  return isNaN(total) || !isFinite(total) ? 0 : total;
};

const quoteBuilderSlice = createSlice({
  name: 'quoteBuilder',
  initialState,
  reducers: {
    addSection: (
      state,
      action: PayloadAction<{
        title: string;
        description?: string;
        parentId?: string;
        id?: string;
        column_visibility?: Partial<ColumnVisibility>;
      }>,
    ) => {
      const {
        title,
        description = '',
        parentId,
        id,
        column_visibility,
      } = action.payload;

      const visibility: ColumnVisibility = {
        ...DEFAULT_COLUMN_VISIBILITY,
        ...(column_visibility ?? {}),
      };

      const newSection: Section = {
        id:
          id ||
          `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title,
        description,
        lineItems: [],
        sections: [],
        column_visibility: visibility,
      };
      if (!parentId) {
        // Add to root level
        state.sections.push(newSection);
      } else {
        // Add to specific parent section
        const parentSection = findSectionById(state.sections, parentId);
        if (parentSection) {
          parentSection.sections.push(newSection);
        }
      }
    },

    removeSection: (state, action: PayloadAction<{ sectionId: string }>) => {
      const { sectionId } = action.payload;
      state.sections = removeSectionById(state.sections, sectionId);
    },

    addLineItem: (
      state,
      action: PayloadAction<{ sectionId: string; lineItem: LineItem }>,
    ) => {
      const { sectionId, lineItem } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        section.lineItems.push(lineItem);
        // Track the newly added line item ID for auto-focus
        state.lastAddedLineItemId = lineItem.id;
      }
    },

    removeLineItem: (
      state,
      action: PayloadAction<{ sectionId: string; lineItemId: string }>,
    ) => {
      const { sectionId, lineItemId } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        section.lineItems = section.lineItems.filter(
          (item) => item.id !== lineItemId,
        );
      }
    },

    updateLineItem: (
      state,
      action: PayloadAction<{
        sectionId: string;
        lineItemId: string;
        updates: Partial<LineItem>;
      }>,
    ) => {
      const { sectionId, lineItemId, updates } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        const lineItemIndex = section.lineItems.findIndex(
          (item) => item.id === lineItemId,
        );
        if (lineItemIndex !== -1) {
          section.lineItems[lineItemIndex] = {
            ...section.lineItems[lineItemIndex],
            ...updates,
          };
        }
      }
    },

    // Universal method to update any field and auto-calculate total
    updateLineItemField: (
      state,
      action: PayloadAction<{
        sectionId: string;
        lineItemId: string;
        field: keyof LineItem;
        value: any;
      }>,
    ) => {
      const { sectionId, lineItemId, field, value } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        const lineItemIndex = section.lineItems.findIndex(
          (item) => item.id === lineItemId,
        );
        if (lineItemIndex !== -1) {
          const currentItem = section.lineItems[lineItemIndex];

          // Sanitize value for numeric fields
          const safeValue = sanitizeFieldValue(field, value);

          const updatedItem = {
            ...currentItem,
            [field]: safeValue,
          };

          // Auto-calculate total when relevant fields change
          if (
            [
              'quantity',
              'unit_price',
              'unit_cost',
              'markup',
              'discount',
              'tax',
            ].includes(field)
          ) {
            const values = getCalculationValues(currentItem, field, safeValue);
            updatedItem.total = calculateTotal(values);
          }

          section.lineItems[lineItemIndex] = updatedItem;
        }
      }
    },

    // Add action to set sections from API data
    setSections: (state, action: PayloadAction<Section[]>) => {
      state.sections = action.payload;
    },

    // Add action to update section title
    updateSectionTitle: (
      state,
      action: PayloadAction<{ sectionId: string; title: string }>,
    ) => {
      const { sectionId, title } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        section.title = title;
      }
    },

    // Add action to update section description
    updateSectionDescription: (
      state,
      action: PayloadAction<{ sectionId: string; description: string }>,
    ) => {
      const { sectionId, description } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        section.description = description;
      }
    },

    // Add action to update section details (title and/or description)
    updateSectionDetails: (
      state,
      action: PayloadAction<{
        sectionId: string;
        title?: string;
        description?: string;
      }>,
    ) => {
      const { sectionId, title, description } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        if (title !== undefined) {
          section.title = title;
        }
        if (description !== undefined) {
          section.description = description;
        }
      }
    },

    // Add action to update column visibility
    updateColumnVisibility: (
      state,
      action: PayloadAction<{
        sectionId: string;
        column: keyof ColumnVisibility;
        visible: boolean;
      }>,
    ) => {
      const { sectionId, column, visible } = action.payload;
      const section = findSectionById(state.sections, sectionId);
      if (section) {
        section.column_visibility[column] = visible;
      }
    },

    // Add action to reset sections to initial state
    resetSections: (state) => {
      state.sections = initialState.sections;
    },

    // Add action to clear last added line item ID (used after focus is applied)
    clearLastAddedLineItemId: (state) => {
      state.lastAddedLineItemId = null;
    },
  },
});

export const {
  addSection,
  addLineItem,
  updateLineItem,
  updateLineItemField,
  removeSection,
  removeLineItem,
  setSections,
  resetSections,
  updateSectionTitle,
  updateSectionDescription,
  updateSectionDetails,
  updateColumnVisibility,
  clearLastAddedLineItemId,
} = quoteBuilderSlice.actions;
export default quoteBuilderSlice.reducer;

// Selectors
export const selectSections = (state: { quoteBuilder: QuoteBuilderState }) =>
  state.quoteBuilder.sections;

export const selectSectionById = (
  state: { quoteBuilder: QuoteBuilderState },
  sectionId: string,
) => {
  return findSectionById(state.quoteBuilder.sections, sectionId);
};

export const selectSectionTitle = (
  state: { quoteBuilder: QuoteBuilderState },
  sectionId: string,
) => {
  const section = findSectionById(state.quoteBuilder.sections, sectionId);
  return section?.title || '';
};

export const selectSectionDescription = (
  state: { quoteBuilder: QuoteBuilderState },
  sectionId: string,
) => {
  const section = findSectionById(state.quoteBuilder.sections, sectionId);
  return section?.description || '';
};

export const selectLastAddedLineItemId = (state: { quoteBuilder: QuoteBuilderState }) =>
  state.quoteBuilder.lastAddedLineItemId;

// Export types for use in components
export type { LineItem, Section, ColumnVisibility };
